import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';
import { useGetInvitationUrlMutation } from '../../../../api/useGetInvitationUrlMutation';
import { IconButton } from '../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Tooltip } from '../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { toast } from '../../../../../CommonComponents/OPL/Toast/Toast';
import { currentClientSelector } from '../../../../../AdviserArea/AdviserAreaSelector';
import { InvitationUrlWrapper } from './ClientUrlInvitation.styles';
import { FormValues, validation } from './ClientUrlInvitation.meta';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';

interface Props {
  clientId: string;
  onSuccess(): void;
  onBack(): void;
  onClose(): void;
}

export const ClientUrlInvitation = ({ clientId, onSuccess, onClose, onBack }: Props) => {
  const [isComplete, setIsComplete] = useState(false);

  const invitationUrlRef = useRef<HTMLDivElement>(null);

  const currentClient = useSelector(currentClientSelector);

  const [isCopiedToClipboard, setIsCopiedToClipboard] = useState(false);

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      email: currentClient?.Email || '',
    },
    resolver: yupResolver(validation),
  });

  const {
    data: getInvitationUrlData,
    mutateAsync: getInvitationUrlMutation,
    isLoading: isGettingInvitationUrl,
    error: getInvitationUrlError,
    reset: resetGetInvitationUrlMutation,
  } = useGetInvitationUrlMutation();

  const onSubmit = async ({ email }: FormValues) => {
    try {
      await getInvitationUrlMutation({ clientId, email });

      if (invitationUrlRef.current) {
        invitationUrlRef.current.scrollIntoView({ block: 'start' });
      }

      onSuccess();

      setIsComplete(true);
    } catch (err) {
      console.error(err);
    }
  };

  const onUrlCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);

      if (!isCopiedToClipboard) {
        setIsCopiedToClipboard(true);
        setTimeout(() => setIsCopiedToClipboard(false), 1000);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to copy the URL to the clipboard, please copy it manually.');
    }
  };

  return (
    <>
      <ModalBody css="height: 200px">
        {getInvitationUrlError && (
          <Alert
            message={getInvitationUrlError.message}
            onRequestClose={resetGetInvitationUrlMutation}
            variant="error"
            scrollIntoViewOptions={{ block: 'start' }}
            my="16"
          />
        )}

        <Box as="form" id="url--invite-client" onSubmit={handleSubmit(onSubmit)} mt="16">
          <Input
            {...register('email')}
            label="Email for URL invitation:"
            type="text"
            formVariant={errors.email ? 'error' : undefined}
            helperText={errors.email?.message}
          />
        </Box>

        {getInvitationUrlData?.InvitationUrl && (
          <InvitationUrlWrapper as="section" ref={invitationUrlRef}>
            <Input
              label="URL for client invitation:"
              readOnly
              value={getInvitationUrlData.InvitationUrl}
            />

            <Tooltip
              content={isCopiedToClipboard ? 'Copied!' : 'Copy URL'}
              hideOnClick={false}
              placement="top"
            >
              <IconButton
                variant="secondary"
                icon="far fa-copy"
                onClick={() => onUrlCopy(getInvitationUrlData.InvitationUrl)}
                aria-label={isCopiedToClipboard ? 'Copied!' : 'Copy URL'}
                css="height: 36px"
              />
            </Tooltip>
          </InvitationUrlWrapper>
        )}
      </ModalBody>

      <ModalFooter hasBorder>
        <Button variant="secondary" leftIcon="fas fa-chevron-left" onClick={onBack} mr="auto">
          Back
        </Button>

        <Button variant="subtleInactive" onClick={onClose} mr="8">
          {isComplete ? 'Close' : 'Cancel'}
        </Button>

        <Button type="submit" form="url--invite-client" isLoading={isGettingInvitationUrl}>
          Get URL
        </Button>
      </ModalFooter>
    </>
  );
};
